import throttle from "lodash/throttle";
import React, {
  type ComponentProps,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import Helmet from "@/components/helmet";
import { cn } from "@/utils/cn";
import { config } from "@/utils/config";

import { CardHeader, CardTitle } from "../card/card";

type ColumnHeaderProps = Pick<
  ColumnProps,
  | "label"
  | "labelElement"
  | "backHref"
  | "className"
  | "action"
  | "withBackButton"
>;

/** Contains the column title with optional back button. */
const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  label,
  labelElement,
  backHref,
  className,
  action,
  withBackButton,
}) => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    if (backHref) {
      navigate(backHref);
      return;
    }

    navigate(-1);
  };

  const cardHeaderProps: ComponentProps<typeof CardHeader> = {};
  if (withBackButton) {
    cardHeaderProps.onBackClick = handleBackClick;
  }

  return (
    <CardHeader className={className} {...cardHeaderProps}>
      {labelElement || <CardTitle title={label} />}

      {action && <div className="flex grow justify-end">{action}</div>}
    </CardHeader>
  );
};

export interface ColumnProps {
  /** Route the back button goes to. */
  backHref?: string;
  /** Column title text. */
  label?: string;
  /** Label element. */
  labelElement?: React.ReactNode;
  /** Should center the labelElement */
  labelElementCentered?: boolean;
  /** Whether this column should have a title and back button. */
  withHeader?: boolean;
  withBackButton?: boolean;
  /** Extra class name for top <div> element. */
  className?: string;
  /** Extra class name for the <CardBody> element. */
  bodyClassName?: string;
  /** Extra class name for the <ColumnHeader> element. */
  headerClassName?: string;
  /** Children to display in the column. */
  children?: React.ReactNode;
  /** Action for the ColumnHeader, displayed at the end. */
  action?: React.ReactNode;
  content?: "list";
  ref?: React.Ref<HTMLDivElement>;
}

/** A backdrop for the main section of the UI. */
const Column = (props: ColumnProps) => {
  const {
    backHref,
    children,
    content,
    label,
    labelElement,
    labelElementCentered = false,
    withHeader = true,
    withBackButton = true,
    className,
    bodyClassName,
    headerClassName,
    action,
    ref,
  } = props;

  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback(
    throttle(() => {
      setIsScrolled(window.pageYOffset > 32);
    }, 50),
    [],
  );

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div role="region" className="relative h-full" ref={ref} aria-label={label}>
      <Helmet title={label}>
        {config.appleAppId && (
          <meta
            data-react-helmet="true"
            name="apple-itunes-app"
            content={`app-id=${config.appleAppId}, app-argument=${location.href}`}
          />
        )}
      </Helmet>

      <div className={className}>
        {withHeader && (
          <ColumnHeader
            label={label}
            labelElement={labelElement}
            backHref={backHref}
            className={cn(
              "sticky top-16 z-50 h-16 border-b border-solid bg-white/90 backdrop-blur-sm lg:top-0 lg:pt-4 dark:bg-gray-900/90",
              {
                "p-4": withHeader || !!labelElement,
                "border-transparent": !isScrolled,
                "border-border": isScrolled,
                "items-center! justify-center!": labelElementCentered,
              },
              headerClassName,
            )}
            withBackButton={withBackButton}
            action={action}
          />
        )}

        <div
          className={cn(bodyClassName, {
            "p-4": content !== "list",
            "pb-20": true, // Extra padding at bottom to account for Chat Pane + Thumb navigation
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export { Column, ColumnHeader };
